import { Locales } from '@/constants/commons'

type AddressContext = {
  area: string
  address: string
  full_address: string
  lat: number
  lng: number
  google_place_id?: string
  locale_address?: string
}

export function useAddress(_data: MaybeRef<Partial<AddressContext>>) {
  const config = useRuntimeConfig()

  const i18n = useI18n()
  const data = computed(() => unref(_data))

  const fullAddress = computed(() => {
    if (data.value?.full_address) return data.value.full_address
    // fallback to area and address
    switch (i18n.locale.value) {
      case Locales.TW:
      case Locales.CN:
      case Locales.JA:
        return `${data.value?.area}${data.value?.address}`
      case Locales.THAI:
        return `${data.value?.address} ${data.value?.area}`
      default:
        return `${data.value?.address}, ${data.value?.area}`
    }
  })

  const googleMapAddress = computed(() => data.value.locale_address || fullAddress.value)
  const coordinates = computed(() => `${data.value?.lat},${data.value?.lng}`)

  const googleMapEmbedSrc = computed(() => {
    const baseUrl = new URL('https://www.google.com/maps/embed/v1/place')
    const searchParams = new URLSearchParams({
      key: config.public.Google_Map_Key,
      q: data.value?.google_place_id ? `place_id:${data.value.google_place_id}` : googleMapAddress.value,
      center: coordinates.value,
      zoom: '16',
      language: i18n.locale.value,
    })

    baseUrl.search = searchParams.toString()

    return baseUrl.href
  })

  const googleMapDirLink = computed(() => {
    const baseUrl = new URL('https://www.google.com/maps/dir/')

    /**
     * * https://developers.google.com/maps/documentation/urls/get-started#forming-the-url
     * api=1 is required for the Google Maps URL scheme to parse parameters.
     */
    const searchParams = new URLSearchParams({
      api: '1',
      destination: googleMapAddress.value,
    })

    if (data.value?.google_place_id) {
      searchParams.append('destination_place_id', data.value?.google_place_id)
    }

    baseUrl.search = searchParams.toString()
    return baseUrl.href
  })

  return {
    fullAddress,
    coordinates,
    googleMapEmbedSrc,
    googleMapDirLink,
  }
}
